import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useViewSingleCategory } from "../../hooks/categorys/category.show";
import FallbackImage from "../../components/common/fallbackImage";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (["true", "1", "active", "enabled", "yes"].includes(s)) return true;
    if (["false", "0", "inactive", "disabled", "no"].includes(s)) return false;
  }
  return false;
};

const ViewCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useViewSingleCategory(id);

  const category =
    data?.data?.category ||
    data?.data ||
    data?.category ||
    data ||
    null;

  // Support both 'catlogs' and 'catelogs' field names from API
  const catlogs = category?.catlogs || category?.catelogs || [];

  if (isLoading) return <div>Loading category...</div>;
  if (isError || !category) return <div>Error loading category. Please go back and try again.</div>;

  return (
    <>
      <h5>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back to Categories
        </button>
      </h5>

      <div className="page-body" style={{ marginTop: 30 }}>
        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "10px",
            background: "#fff",
            width: "100%",
          }}
        >
          {/* IMAGE */}
          {(() => {
            const imgSrc = category?.image?.url || category?.image;
            if (!imgSrc) return null;
            return (
              <FallbackImage
                src={imgSrc}
                alt="Category"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                  marginBottom: "20px",
                  borderRadius: "10px",
                  border: "1px solid #eee"
                }}
              />
            );
          })()}

          {/* DETAILS */}
          <div className="client-grid">
            <div className="field">
              <span className="label">Title</span>
              <span className="value">{category?.title || "-"}</span>
            </div>

            <div className="field">
              <span className="label">Description</span>
              <span className="value">{category?.description || "-"}</span>
            </div>

            <div className="field">
              <span className="label">Location</span>
              <span className="value">{category?.location || "-"}</span>
            </div>

            {/* ✅ CATALOGS */}
            <div className="field">
              <span className="label">Catalogs</span>

              <div className="value">
                {catlogs.length ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {catlogs.map((cat, index) => {
                      const rawFile = cat.file?.url || cat.file;
                      const fileUrl = rawFile ? resolveImageUrl(rawFile) : null;
                      const fileName = typeof rawFile === "string" ? rawFile.split("/").pop() : "Download File";

                      return (
                        <div
                          key={cat._id || index}
                          style={{
                            padding: "12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "10px",
                            background: "#f9fafb",
                          }}
                        >
                          <div
                            style={{ fontWeight: "600", marginBottom: "4px" }}
                          >
                            {cat.title || "No Title"}
                          </div>

                          <div
                            style={{
                              fontSize: "14px",
                              color: "#555",
                              marginBottom: "6px",
                            }}
                          >
                            {cat.description || "No Description"}
                          </div>

                          {fileUrl && (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#5C308D", fontSize: "14px", display: "flex", alignItems: "center", gap: "5px" }}
                            >
                              📄 {fileName}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  "-"
                )}
              </div>
            </div>

            
            <div className="field">
              <span className="label">Status</span>
              <span className="value">
                {toBool(category?.isActive ?? category?.status)
                  ? "Active"
                  : "Inactive"}
              </span>
            </div>


            
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewCategory;
